import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Check,
  Trash2,
  Loader2,
  Shield,
  AlertTriangle,
  X,
} from 'lucide-react';
import { adminIntegrationHelpers, type IntegrationView } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm-provider';

/**
 * Admin Integrations — superadmin manages HMAC signing secrets for
 * service-to-service integrations (YOM, Chatbot, third-party vendors).
 *
 * See docs/PLUGIN-CONTRACT.md §10.
 */
export default function AdminIntegrations() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [creating, setCreating] = useState(false);
  const [viewingSecret, setViewingSecret] = useState<{
    id: string;
    serviceId: string;
    secret: string;
    context: 'created' | 'rotated' | 'revealed';
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-integrations'],
    queryFn: () => adminIntegrationHelpers.list(),
  });

  const integrations: IntegrationView[] = (data?.data as any)?.data ?? [];

  const rotateMutation = useMutation({
    mutationFn: (id: string) => adminIntegrationHelpers.rotate(id),
    onSuccess: (response, id) => {
      const secret = (response.data as any)?.secret;
      const serviceId = integrations.find((i) => i.id === id)?.serviceId || 'unknown';
      setViewingSecret({ id, serviceId, secret, context: 'rotated' });
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Rotate failed');
    },
  });

  const revealMutation = useMutation({
    mutationFn: (id: string) => adminIntegrationHelpers.reveal(id),
    onSuccess: (response, id) => {
      const data = (response.data as any)?.data;
      setViewingSecret({
        id,
        serviceId: data.serviceId,
        secret: data.secret,
        context: 'revealed',
      });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Reveal failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminIntegrationHelpers.delete(id),
    onSuccess: () => {
      toast.success('Integration deleted');
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Delete failed');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (args: { id: string; status: 'active' | 'disabled' }) =>
      adminIntegrationHelpers.update(args.id, { status: args.status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="text-primary h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-red-500" />
        <p className="text-muted-foreground text-sm">
          Failed to load integrations. Make sure you're logged in as a superadmin.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">Integrations</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            HMAC signing secrets for service-to-service authentication. Used by YOM, Chatbot, and
            any third-party plugin vendor that integrates with Tyashin.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          New integration
        </button>
      </div>

      {/* Empty state */}
      {integrations.length === 0 ? (
        <div className="bg-card rounded-xl border p-10 text-center">
          <Shield className="text-muted-foreground mx-auto mb-3 h-10 w-10" />
          <h3 className="font-semibold">No integrations yet</h3>
          <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">
            Create one to generate an HMAC signing secret for a service like YOM, AI Chatbot, or a
            third-party plugin vendor. You'll be shown the secret once — copy it to the other
            service's environment.
          </p>
        </div>
      ) : (
        <div className="bg-card overflow-hidden rounded-xl border">
          <table className="w-full">
            <thead className="bg-muted/50 border-b text-sm">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Service</th>
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Last rotated</th>
                <th className="px-4 py-3 text-left font-medium">Last used</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {integrations.map((it) => (
                <tr key={it.id} className="border-b text-sm last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.displayName}</div>
                    <div className="text-muted-foreground mt-0.5 font-mono text-xs">
                      {it.serviceId}
                    </div>
                    {it.notes && (
                      <div className="text-muted-foreground mt-1 text-xs">{it.notes}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
                        it.isThirdParty
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}
                    >
                      {it.isThirdParty ? 'Third-party' : 'Internal'}
                    </span>
                    {it.developerEmail && (
                      <div className="text-muted-foreground mt-1 text-xs">{it.developerEmail}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() =>
                        toggleMutation.mutate({
                          id: it.id,
                          status: it.status === 'active' ? 'disabled' : 'active',
                        })
                      }
                      disabled={toggleMutation.isPending}
                      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
                        it.status === 'active'
                          ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-400'
                      }`}
                    >
                      {it.status === 'active' ? 'Active' : 'Disabled'}
                    </button>
                  </td>
                  <td className="text-muted-foreground px-4 py-3 text-xs">
                    {it.lastRotatedAt ? new Date(it.lastRotatedAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="text-muted-foreground px-4 py-3 text-xs">
                    {it.lastUsedAt ? new Date(it.lastUsedAt).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => revealMutation.mutate(it.id)}
                        disabled={revealMutation.isPending}
                        title="Reveal secret"
                        className="hover:bg-muted rounded p-1.5 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (
                            await confirm({
                              title: 'Rotate secret?',
                              message: `Rotate secret for ${it.displayName}? The old secret will be invalidated immediately. You'll need to update the integrated service with the new secret.`,
                              danger: true,
                            })
                          ) {
                            rotateMutation.mutate(it.id);
                          }
                        }}
                        disabled={rotateMutation.isPending}
                        title="Rotate secret"
                        className="hover:bg-muted rounded p-1.5 transition-colors"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (
                            await confirm({
                              title: 'Delete integration?',
                              message: `Delete the integration for ${it.displayName}? The signing secret will be permanently removed. Requests from the integrated service will start failing immediately.`,
                              danger: true,
                            })
                          ) {
                            deleteMutation.mutate(it.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        title="Delete integration"
                        className="rounded p-1.5 text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <CreateIntegrationModal
          onClose={() => setCreating(false)}
          onSuccess={(secret, serviceId, id) => {
            setCreating(false);
            setViewingSecret({ id, serviceId, secret, context: 'created' });
            queryClient.invalidateQueries({ queryKey: ['admin-integrations'] });
          }}
        />
      )}

      {/* Secret reveal modal */}
      {viewingSecret && (
        <SecretRevealModal
          serviceId={viewingSecret.serviceId}
          secret={viewingSecret.secret}
          context={viewingSecret.context}
          onClose={() => setViewingSecret(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// Create modal
// =============================================================================

function CreateIntegrationModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (secret: string, serviceId: string, id: string) => void;
}) {
  const [serviceId, setServiceId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [notes, setNotes] = useState('');
  const [isThirdParty, setIsThirdParty] = useState(false);
  const [developerEmail, setDeveloperEmail] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      adminIntegrationHelpers.create({
        serviceId: serviceId.trim(),
        displayName: displayName.trim(),
        notes: notes.trim() || undefined,
        isThirdParty: isThirdParty || undefined,
        developerEmail: isThirdParty && developerEmail.trim() ? developerEmail.trim() : undefined,
      }),
    onSuccess: (response) => {
      const payload = response.data as any;
      onSuccess(payload.secret, payload.data.serviceId, payload.data.id);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Create failed');
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card w-full max-w-md rounded-xl border p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <h3 className="text-lg font-semibold">New integration</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!serviceId || !displayName) return;
            createMutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <label className="mb-1 block text-sm font-medium">
              Service ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value.toLowerCase())}
              placeholder="e.g. yom, chatbot, acme-crm"
              pattern="[a-z0-9-]+"
              required
              className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Lowercase letters, numbers, hyphens. Must match the plugin id in the manifest for
              plugin-framework plugins. This is how Tyashin looks up the secret at runtime.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">
              Display name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. YourOwnMetrics (YOM)"
              required
              className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional — visible only to other superadmins"
              className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="third-party"
              checked={isThirdParty}
              onChange={(e) => setIsThirdParty(e.target.checked)}
            />
            <label htmlFor="third-party" className="text-sm">
              Third-party developer integration
            </label>
          </div>

          {isThirdParty && (
            <div>
              <label className="mb-1 block text-sm font-medium">Developer email</label>
              <input
                type="email"
                value={developerEmail}
                onChange={(e) => setDeveloperEmail(e.target.value)}
                placeholder="developer@vendor.com"
                className="bg-background w-full rounded-lg border px-3 py-2 text-sm"
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-muted hover:bg-muted/80 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !serviceId || !displayName}
              className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create & generate secret
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Secret reveal modal
// =============================================================================

function SecretRevealModal({
  serviceId,
  secret,
  context,
  onClose,
}: {
  serviceId: string;
  secret: string;
  context: 'created' | 'rotated' | 'revealed';
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const heading =
    context === 'created'
      ? 'Integration created'
      : context === 'rotated'
        ? 'Secret rotated'
        : 'Signing secret';

  const subheading =
    context === 'created'
      ? 'Copy this secret now. It is stored encrypted — you can reveal it again later, but keep it safe.'
      : context === 'rotated'
        ? 'Old secret invalidated. Update the integrated service with this new value immediately.'
        : 'Current HMAC signing secret for this integration.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card w-full max-w-lg rounded-xl border p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">{heading}</h3>
            <p className="text-muted-foreground mt-1 text-sm">{subheading}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-3">
          <label className="text-muted-foreground mb-1 block text-xs font-medium">Service ID</label>
          <code className="bg-muted block rounded px-3 py-2 font-mono text-sm">{serviceId}</code>
        </div>

        <div className="mb-4">
          <label className="text-muted-foreground mb-1 block text-xs font-medium">
            HMAC signing secret
          </label>
          <div className="bg-muted flex items-center gap-2 rounded px-3 py-2 font-mono text-xs">
            <span className="flex-1 break-all">{visible ? secret : secret.replace(/./g, '•')}</span>
            <button
              onClick={() => setVisible((v) => !v)}
              className="text-muted-foreground hover:text-foreground"
              title={visible ? 'Hide' : 'Show'}
            >
              {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              onClick={copy}
              className="text-primary hover:underline"
              title="Copy to clipboard"
            >
              {copied ? (
                <span className="flex items-center gap-1">
                  <Check className="h-3 w-3" /> Copied
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <Copy className="h-3 w-3" /> Copy
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm dark:border-yellow-800 dark:bg-yellow-900/20">
          <p className="mb-1 flex items-center gap-1.5 font-medium text-yellow-800 dark:text-yellow-400">
            <AlertTriangle className="h-4 w-4" />
            Next steps
          </p>
          <ul className="text-muted-foreground ml-5 list-disc space-y-0.5 text-xs">
            <li>
              Copy this secret to the <strong>{serviceId}</strong> service's environment
            </li>
            <li>Both sides MUST use the same value for signature verification to work</li>
            <li>
              If you lose it, rotate from this page — the old value is invalidated immediately
            </li>
          </ul>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
